import React, {useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import values from 'lodash/values';
import {
  DataDtmActions,
  DirectoryDescriptionTrimmedLengths,
  ExhibitorFeatures,
  SessionStorageKeys
} from '../../common/Constants';
import StringFormatter from '../../common/StringFormatter';
import DtmTags from '../../common/dtmTags';
import Classes from '../../common/Classes';
import Tags from '../Tags';
import Brands from './components/Brands';
import Name from './components/Name';
import Stand from './components/Stand';
import MainStandHolderName from './components/MainStandHolderName';
import FavouriteComponent from './components/FavouriteComponent';
import ExhibitorInfo from './components/ExhibitorInfo';
import Label from '../../common/components/Label';
import FollowComponent from './components/FollowComponent';
import Utils from '../../common/Utils';
import mixpanelConstants from '../../common/mixpanelConstants';
import {trackImpressions, trackMixpanelEvent} from '../../common/MixpanelHelper';
import MediaQuery from 'react-responsive';
import ExhibitorLogo from './components/ExhibitorLogo';
import InlineRecommendationButton from './components/InlineRecommendationButton';

const ExhibitorBronzePackage = ({
  exhibitor, translations, detailPageLinkFormat, defaultLogoUrl, isInShowPlan, showPlanningEnabled, showFollowButton,
  interfaceLocale, baseApiUrl, digitalPortfolioId, isFollowing, handleFollowClick, userId, eventEditionId,
  hideStandInfo,
  isMercuryParticipantShow, showExhibitorRecommendation
}) => {
  const mixPanelProps = {exhibitorId: exhibitor.id, exhibitorName: exhibitor.name, packageName: Utils.getPackageName(exhibitor.packageId),
    component: mixpanelConstants.component.exhibitorDirectory, subComponent: mixpanelConstants.subComponent.standard};

  let triggerMixpanelEvent = (element) => {
    let props = {...mixPanelProps, element: element}
    if(element === mixpanelConstants.element.mainStandHolderTitle){
      props = {...props, exhibitorId: exhibitor.parentId, exhibitorName: exhibitor.mainStandHolderName, packageName: Utils.getPackageName(exhibitor.mainStandHolderPackageId)}
    }
    trackMixpanelEvent(mixpanelConstants.eventName.exhibitorClicked, props)
  }
  let detailPageLink;
  const elemExhRef = useRef();

  useEffect(() => {
    const observer = trackImpressions(mixpanelConstants.eventName.exhibitorImpression, mixPanelProps, 0);
    if (elemExhRef.current) {
      observer.observe(elemExhRef.current)
    }
    return () => {
      observer.disconnect();
    };
  }, []);

  const environment = Utils.getCurrentEnvironment();
  if (!Utils.isDevEnvironment(environment) && !Utils.isShowEnvironment(environment) && exhibitor.name) {
    //replaces spaces with '-' from company name with meaning full name
    let exhibitorName = Utils.getSEOFriendlyName(exhibitor.name);
    detailPageLink = StringFormatter.format(detailPageLinkFormat, exhibitorName.concat('.', exhibitor.organisationGuid));
  } else {
    detailPageLink = StringFormatter.format(detailPageLinkFormat, exhibitor.organisationGuid);
  }
  const getFeaturesInOrder = () => {
    let features = [];
    let exhibitorFeatures = exhibitor.features ? exhibitor.features : []
    if (exhibitorFeatures.includes(ExhibitorFeatures.New)) {
      features.push(
          <Label
              featureNameKey={ExhibitorFeatures.New}
              translations={translations.features}
              tags={DtmTags.exhibitorDirectory}
              exhibitorId={exhibitor.id}
          />
      )
    }

    return features;
  }
  const excludeTagCharacter = '>';
  const features = getFeaturesInOrder()
  const isPrimaryEventEdition = Utils.getFromSessionStorage(SessionStorageKeys.isPrimaryEventEdition);
  const initialState = (isPrimaryEventEdition || isMercuryParticipantShow) ? isFollowing : isInShowPlan;
  const translationsForFavouritecomponent = (isPrimaryEventEdition || isMercuryParticipantShow) ? translations.follow : translations.favourite;
  const exhibitorDetailContainerClassNames = showExhibitorRecommendation ? classnames(Classes.exhibitorRow, Classes.exhibitorDetailContainerPadding) : Classes.exhibitorRow;

  function isPpsAnswersExists() {
    return exhibitor.ppsAnswers && exhibitor.ppsAnswers.length > 0;
  }

  function getTagComponent(showHeading = true) {
    return <>
      {isPpsAnswersExists() ? (
          <Tags
              showHeading={showHeading}
              tags={exhibitor.ppsAnswers.map(answer => (answer.includes(': ') ? answer.split(': ')[1] : answer))}
              detailPageLink={detailPageLink}
              triggerMixpanelEvent={triggerMixpanelEvent}
              categoriesLabel={translations.categoriesLabel}
          />
      ) : (
          <Tags
              showHeading={showHeading}
              tags={values(exhibitor.filters)
                  .reduce((acc, cur) => acc.concat(...values(cur)), [])
                  .filter(val => !val.includes(excludeTagCharacter))
                  .map(tag => (tag.includes(': ') ? tag.split(': ').slice(1).join(': ') : tag))}
              detailPageLink={detailPageLink}
              triggerMixpanelEvent={triggerMixpanelEvent}
              categoriesLabel={translations.categoriesLabel}
          />
      )}
    </>;
  }

  function getFeatures() {
    return <>
      {
          features.length > 0 &&
          <div className={Classes.exhibitorFeaturesContainer}>
            {features}
          </div>
      }
    </>;
  }

  function getFollowAndFavourite() {
    return <>
      {
        isMercuryParticipantShow || (!isPrimaryEventEdition || showFollowButton) ?
            <FavouriteComponent
                exhibitor={exhibitor}
                translations={translationsForFavouritecomponent}
                showPlanningEnabled={showPlanningEnabled}
                initialState={initialState}
                className={Classes.showPlanContainer}
                dtmData={DtmTags.exhibitorDirectory}
                isPrimaryEventEdition={isPrimaryEventEdition}
                isMercuryParticipantShow={isMercuryParticipantShow}
                handleFollowClick={handleFollowClick}
                locale={interfaceLocale}
                type="exhibitor"
                component={mixpanelConstants.component.exhibitorDirectory}
            /> : null
      }
      {!isMercuryParticipantShow && (showFollowButton && !isPrimaryEventEdition) ?
          <FollowComponent
              isFollowed={isFollowing}
              translations={translations.follow}
              locale={interfaceLocale}
              baseApiUrl={baseApiUrl}
              digitalPortfolioId={digitalPortfolioId}
              organisationId={exhibitor.organisationGuid}
              handleFollowClick={handleFollowClick}
              dtmData={DtmTags.exhibitorDirectory}
              userId={userId}
              exhibitingOrganisationId={exhibitor.id}
          /> : null
      }
    </>;
  }

  function getStands() {
    return <>
      {exhibitor.standReference &&
          exhibitor.standReference.trim().length > 0 && !hideStandInfo &&
          <Stand
            standReference={exhibitor.standReference}
            translations={translations}
            dtmTags={DtmTags.exhibitorDirectory.stands}
            exhibitorId={JSON.stringify(exhibitor.id)}
            mixpanelProps={{
              exhibitorId: exhibitor.id,
              exhibitorName: exhibitor.companyName,
              packageName: Utils.getPackageName(exhibitor.packageId),
              component: mixpanelConstants.component.exhibitorDirectory,
              element: mixpanelConstants.element.moreStands,
              subComponent: mixpanelConstants.subComponent.standard
            }}
          />
      }
    </>;
  }

  function getContactContainer() {
    return <div className={Classes.exhibitorContactContainer}>
      {getStands()}
      {getFollowAndFavourite()}
    </div>;
  }

  function getExhibitorSummary() {
    return <div className={Classes.exhibitorSummary}>
      <Name
        exhibitor={exhibitor}
        detailPageLink={detailPageLink}
        tags={DtmTags.exhibitorDirectory.bronze}
        triggerMixpanelEvent={triggerMixpanelEvent}
      />
      {getFeatures()}
      <div className={Classes.description}>
        <ExhibitorInfo
            showHeading={false}
            detailPageLink={detailPageLink}
            id={exhibitor.id}
            tag={DtmTags.exhibitorDirectory.bronze.description}
            label={translations.descriptionTitle}
            content={exhibitor.description}
            contentLength={DirectoryDescriptionTrimmedLengths.bronze}
            triggerMixpanelEvent={triggerMixpanelEvent}
            element={mixpanelConstants.element.description}
            testId="show-description"
        />
        <Brands
            representedBrands={exhibitor.representedBrands}
            translations={translations}
            detailPageLink={detailPageLink}
            id={exhibitor.id}
            tag={DtmTags.exhibitorDirectory.bronze.representedBrands}
            triggerMixpanelEvent={triggerMixpanelEvent}
        />
        {getTagComponent(true)}
      </div>
    </div>;
  }

  return (
      <div
          style={{'--brand-color': window.reactSettings.props.themes.brandPrimary}}
          data-testId="exh-bronze-desktop"
          className={
            showExhibitorRecommendation ? classnames(Classes.directoryItem, Classes.directoryItemFeatureToggled, Classes.exhibitorCategory, Classes.exhibitorDirectoryRecommendationFeatureToggled) : classnames(Classes.directoryItem, Classes.directoryItemFeatureToggled, Classes.exhibitorCategory)
          }
          data-dtm={DtmTags.exhibitorDirectory.bronze.exhibitor}
          data-dtm-attributes={JSON.stringify({ exhibitorId: exhibitor.id })}
          ref={elemExhRef}
      >
        <div
            data-dtm={DtmTags.exhibitorDirectory.bronze.exhibitor}
            data-dtm-attributes={JSON.stringify({ exhibitorId: exhibitor.id })}
            data-dtm-actions={DataDtmActions.view}
            className={exhibitorDetailContainerClassNames}
        >
          <MediaQuery minWidth={1199}>
            <div className={Classes.exhibitorDetailContainer}>
              <ExhibitorLogo
                  exhibitor={exhibitor}
                  detailPageLink={detailPageLink}
                  defaultLogoUrl={defaultLogoUrl}
                  tags={DtmTags.exhibitorDirectory.bronze}
                  triggerMixpanelEvent={triggerMixpanelEvent}
              />
              {getExhibitorSummary()}
              {getContactContainer()}
            </div>
            {exhibitor.mainStandHolderName &&
                <MainStandHolderName
                    exhibitor={exhibitor}
                    translations={translations}
                    detailPageLinkFormat={detailPageLinkFormat}
                    tags={DtmTags.exhibitorDirectory}
                    triggerMixpanelEvent={triggerMixpanelEvent}
                />}
          </MediaQuery>
          <MediaQuery minWidth={768} maxWidth={1198}>
            <div className={Classes.exhibitorDetailContainer}>
              <div className={Classes.logoStandContainer}>
                <ExhibitorLogo
                    exhibitor={exhibitor}
                    detailPageLink={detailPageLink}
                    defaultLogoUrl={defaultLogoUrl}
                    tags={DtmTags.exhibitorDirectory.bronze}
                    triggerMixpanelEvent={triggerMixpanelEvent}
                />
                {getContactContainer()}
              </div>
              {getExhibitorSummary()}
            </div>
            {exhibitor.mainStandHolderName &&
                <MainStandHolderName
                    exhibitor={exhibitor}
                    translations={translations}
                    detailPageLinkFormat={detailPageLinkFormat}
                    tags={DtmTags.exhibitorDirectory}
                    triggerMixpanelEvent={triggerMixpanelEvent}
                />}
          </MediaQuery>
          <MediaQuery maxWidth={767}>
            <div className={Classes.exhibitorDetailContainer}>
              <ExhibitorLogo
                  exhibitor={exhibitor}
                  detailPageLink={detailPageLink}
                  defaultLogoUrl={defaultLogoUrl}
                  tags={DtmTags.exhibitorDirectory.bronze}
                  triggerMixpanelEvent={triggerMixpanelEvent}
              />
              <div className={Classes.exhibitorSummary}>
                <Name
                  exhibitor={exhibitor}
                  detailPageLink={detailPageLink}
                  tags={DtmTags.exhibitorDirectory.bronze}
                  triggerMixpanelEvent={triggerMixpanelEvent}
                />
                <div className={Classes.standContainer}>
                  {getStands()}
                </div>
                <div className={Classes.showPlanBtns}>
                  {getFollowAndFavourite()}
                </div>
              </div>
            </div>
            {getFeatures()}
            {
              exhibitor.description && <div>
                <div className={classnames(Classes.descriptionContainer, Classes.descriptionContainerMargin)}>
                  <div className={Classes.description}>
                    <ExhibitorInfo
                        showHeading={false}
                        detailPageLink={detailPageLink}
                        id={exhibitor.id}
                        tag={DtmTags.exhibitorDirectory.bronze.description}
                        content={exhibitor.description}
                        contentLength={DirectoryDescriptionTrimmedLengths.bronze}
                        triggerMixpanelEvent={triggerMixpanelEvent}
                        element={mixpanelConstants.element.description}
                        testId="show-description"
                    />
                  </div>
                  <div className={Classes.tags}>
                    {getTagComponent(false)}
                  </div>
                </div>
            </div>
            }
          </MediaQuery>
        </div>
        {
              showExhibitorRecommendation && <InlineRecommendationButton exhibitorId={exhibitor.id} exhibitorName={exhibitor.name} translations={translations} interfaceLocale={interfaceLocale}/>
        }
      </div>
  )
}


ExhibitorBronzePackage.propTypes = {
  exhibitor: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  defaultLogoUrl: PropTypes.string.isRequired,
  detailPageLinkFormat: PropTypes.string.isRequired,
  isInShowPlan: PropTypes.bool,
  showPlanningEnabled: PropTypes.bool,
  showFollowButton: PropTypes.bool,
  interfaceLocale: PropTypes.string,
  baseApiUrl: PropTypes.string.isRequired,
  digitalPortfolioId: PropTypes.string,
  isFollowing: PropTypes.bool,
  handleFollowClick: PropTypes.func,
  userId: PropTypes.string,
  eventEditionId: PropTypes.string.isRequired,
  hideStandInfo: PropTypes.bool,
  isMercuryParticipantShow : PropTypes.bool.isRequired,
};

export default ExhibitorBronzePackage;
